import React from "react";
import MarketingPage, { PageHero } from "../../components/marketing/MarketingPage";

const COPY = {
  terms: {
    eyebrow: "Legal",
    title: "Terms of Service",
    description: "The terms that govern your use of Collavue.",
    seo: {
      title: "Terms of Service",
      description: "The terms governing your use of Collavue's AI lead discovery and reply tools.",
      path: "/terms",
      keywords: "Collavue terms, Collavue terms of service, SaaS terms",
    },
    sections: [
      ["Acceptance of terms", "By accessing or using Collavue, you agree to be bound by these terms. If you don't agree, please don't use the service."],
      ["Acceptable use", "You agree not to use Collavue to spam, harass, or violate any platform's rules. All replies sent from Collavue must pass through human approval and comply with the source platform's terms."],
      ["Subscriptions", "Plans are billed monthly or annually. You can cancel at any time from billing settings; access continues through the end of the billing period."],
      ["Termination", "We may suspend or terminate accounts that violate these terms, abuse the service, or expose Collavue to legal risk."],
      ["Liability", "Collavue is provided 'as is.' Our maximum liability is limited to the fees you paid us in the last 12 months."],
      ["Changes", "We may update these terms; we'll notify you of material changes via email or in-app."],
    ],
  },
  privacy: {
    eyebrow: "Legal",
    title: "Privacy Policy",
    description: "What we collect, why we collect it, and the rights you have.",
    seo: {
      title: "Privacy Policy",
      description: "Collavue's privacy policy: what we collect, how we use it, and your rights as a user.",
      path: "/privacy",
      keywords: "Collavue privacy, Collavue GDPR, SaaS privacy policy",
    },
    sections: [
      ["What we collect", "Account info (name, email, workspace), usage data, and the public posts and profiles you ask us to monitor."],
      ["Why we collect it", "To run the service, score leads, train your brand voice on the assets you provide, and improve product performance."],
      ["Sharing", "We never sell your data. We share data only with sub-processors (e.g. cloud hosting, email delivery) that are bound by data-processing agreements."],
      ["Your rights", "You can export, correct, or delete your data at any time. EU/UK users have full GDPR rights including the right to object and request portability."],
      ["Security", "Data is encrypted in transit and at rest. We follow least-privilege access for all internal systems."],
      ["Contact", "Questions? Email privacy@collavue.com."],
    ],
  },
  dpa: {
    eyebrow: "Legal",
    title: "Data Processing Addendum",
    description: "Our DPA for customers processing personal data through Collavue.",
    seo: {
      title: "Data Processing Addendum (DPA)",
      description: "Collavue's Data Processing Addendum for customers subject to GDPR, UK GDPR, or similar data protection laws.",
      path: "/dpa",
      keywords: "Collavue DPA, data processing addendum, GDPR DPA, SaaS DPA",
    },
    sections: [
      ["Scope", "This DPA applies to processing of personal data by Collavue on behalf of customers subject to GDPR, UK GDPR, CCPA, or similar laws."],
      ["Roles", "Customer is the data controller. Collavue is the data processor."],
      ["Sub-processors", "A current list of sub-processors (cloud hosting, email, analytics) is available on request and updated when changes occur."],
      ["International transfers", "We rely on Standard Contractual Clauses (SCCs) and the UK IDTA where applicable."],
      ["Security measures", "Encryption in transit and at rest, access controls, audit logging, and regular penetration testing."],
      ["Sign", "To execute the DPA, contact legal@collavue.iocom. We can countersign within 48 hours."],
    ],
  },
  status: {
    eyebrow: "System status",
    title: "All systems operational",
    description: "Real-time status of Collavue's APIs, integrations, and reply pipelines.",
    seo: {
      title: "Status — Collavue uptime and incident history",
      description: "Live status and incident history for Collavue's API, ingestion pipelines, and integrations.",
      path: "/status",
      keywords: "Collavue status, Collavue uptime, SaaS status page",
    },
    sections: [
      ["API", "Operational · 99.99% over the last 90 days"],
      ["X / Twitter ingestion", "Operational · 99.96% over the last 90 days"],
      ["LinkedIn ingestion", "Operational · 99.95% over the last 90 days"],
      ["Reddit ingestion", "Operational · 99.95% over the last 90 days"],
      ["Reply Agent", "Operational · 99.99% over the last 90 days"],
      ["Recent incidents", "No incidents reported in the last 30 days."],
    ],
  },
};

export default function Legal({ kind }) {
  const data = COPY[kind];
  return (
    <MarketingPage seo={data.seo}>
      <PageHero
        eyebrow={data.eyebrow}
        title={data.title}
        description={data.description}
      />
      <section className="bg-white border-t border-[#e3dccb]">
        <div className="max-w-4xl mx-auto px-6 py-20 space-y-8">
          {data.sections.map(([h, p]) => (
            <div key={h}>
              <h2 className="font-display text-2xl text-[#0a1736] font-semibold">{h}</h2>
              <p className="text-[#5a6478] mt-3 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </section>
    </MarketingPage>
  );
}
